import React, { useState, useEffect, useCallback } from 'react';
import { dataRoomsApi, documentsApi, foldersApi, shareLinksApi, folderTemplatesApi } from '../lib/api';
import { formatDate, PROCUREMENT_SECTORS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import {
  Plus, MagnifyingGlass, Folder, FolderPlus, File, FileDoc, FilePdf, FileImage,
  FileText, Download, Trash, Upload, Eye, Lock, CaretRight, House,
  ShareNetwork, Copy, LinkSimple, SquaresFour, Rows, MagicWand, ListChecks, PencilSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import logger from '../lib/logger';

const FILE_ICONS = {
  'application/pdf': FilePdf,
  'image/': FileImage,
  'text/': FileText,
  'application/msword': FileDoc,
  'application/vnd.openxmlformats': FileDoc,
};

function getFileIcon(contentType) {
  for (const [key, Icon] of Object.entries(FILE_ICONS)) {
    if (contentType?.includes(key)) return Icon;
  }
  return File;
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function isPreviewable(contentType) {
  if (!contentType) return false;
  return contentType.startsWith('image/')
    || contentType === 'application/pdf'
    || contentType.startsWith('text/')
    || contentType.startsWith('video/')
    || contentType.startsWith('audio/');
}

export default function DocumentsPage() {
  const [dataRooms, setDataRooms] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null); // null = root
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Dialogs
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('docs_view_mode') || 'grid');
  const [templates, setTemplates] = useState([]);
  const [templateBusy, setTemplateBusy] = useState(false);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  // Forms
  const [roomFormData, setRoomFormData] = useState({ name: '', description: '', sector: '' });
  const [uploadFormData, setUploadFormData] = useState({ name: '', description: '', access_level: 'private' });
  const [folderForm, setFolderForm] = useState({ name: '' });
  const [shareForm, setShareForm] = useState({ resource_type: 'document', resource_id: '', resource_name: '', allow_download: true, expires_in_days: 14 });
  const [createdShare, setCreatedShare] = useState(null);

  // Preview
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewText, setPreviewText] = useState(null);

  // Confirmation dialog for destructive actions
  const [confirm, setConfirm] = useState(null); // { title, description, onConfirm, busy }

  // Rename dialog state
  const [renameTarget, setRenameTarget] = useState(null); // { type: 'document'|'folder', id, name }
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);

  const fetchDataRooms = useCallback(async () => {
    try {
      const response = await dataRoomsApi.getAll({ limit: 100 });
      setDataRooms(response.data);
    } catch (error) {
      toast.error('Failed to fetch data rooms');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFolders = useCallback(async (roomId, parentId) => {
    if (!roomId) { setFolders([]); return; }
    try {
      const res = await foldersApi.getAll({ data_room_id: roomId, parent_id: parentId || '' });
      setFolders(res.data);
    } catch { setFolders([]); }
  }, []);

  const fetchDocuments = useCallback(async (roomId, folderId) => {
    try {
      const params = { limit: 100 };
      if (roomId) params.data_room_id = roomId;
      if (folderId !== undefined) params.folder_id = folderId || '';
      const response = await documentsApi.getAll(params);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    }
  }, []);

  useEffect(() => { fetchDataRooms(); }, [fetchDataRooms]);

  useEffect(() => { localStorage.setItem('docs_view_mode', viewMode); }, [viewMode]);

  useEffect(() => {
    // When room or current folder changes, refetch
    if (selectedRoom) {
      fetchFolders(selectedRoom.id, currentFolder?.id || null);
      fetchDocuments(selectedRoom.id, currentFolder?.id || null);
    } else {
      fetchDocuments();
      setFolders([]);
    }
  }, [selectedRoom, currentFolder, fetchFolders, fetchDocuments]);

  const openRoom = (room) => {
    setSelectedRoom(room);
    setCurrentFolder(null);
    setBreadcrumb([]);
  };

  const enterFolder = async (folder) => {
    setCurrentFolder(folder);
    try {
      const res = await foldersApi.get(folder.id);
      setBreadcrumb(res.data.breadcrumb || [{ id: folder.id, name: folder.name }]);
    } catch {
      setBreadcrumb([{ id: folder.id, name: folder.name }]);
    }
  };

  const goToBreadcrumb = (idx) => {
    if (idx < 0) {
      setCurrentFolder(null);
      setBreadcrumb([]);
      return;
    }
    const slice = breadcrumb.slice(0, idx + 1);
    const target = slice[slice.length - 1];
    setCurrentFolder({ id: target.id, name: target.name });
    setBreadcrumb(slice);
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    try {
      await dataRoomsApi.create(roomFormData);
      toast.success('Data room created');
      setIsRoomDialogOpen(false);
      setRoomFormData({ name: '', description: '', sector: '' });
      fetchDataRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create data room');
    }
  };

  const handleDeleteRoom = async (id) => {
    setConfirm({
      title: 'Delete this data room?',
      description: 'This will permanently remove the data room and ALL its folders & documents.',
      confirmLabel: 'Delete Room',
      onConfirm: async () => {
        try {
          await dataRoomsApi.delete(id);
          toast.success('Data room deleted');
          if (selectedRoom?.id === id) { setSelectedRoom(null); setCurrentFolder(null); setBreadcrumb([]); }
          fetchDataRooms();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete data room');
        }
      },
    });
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!selectedRoom) return toast.error('Select a data room first');
    try {
      await foldersApi.create({
        name: folderForm.name,
        data_room_id: selectedRoom.id,
        parent_id: currentFolder?.id || null,
      });
      toast.success(currentFolder ? 'Subfolder created' : 'Folder created');
      setIsFolderDialogOpen(false);
      setFolderForm({ name: '' });
      fetchFolders(selectedRoom.id, currentFolder?.id || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    setConfirm({
      title: `Delete folder "${folder.name}"?`,
      description: 'All subfolders and documents inside will be deleted too.',
      confirmLabel: 'Delete Folder',
      onConfirm: async () => {
        try {
          await foldersApi.delete(folder.id);
          toast.success('Folder deleted');
          fetchFolders(selectedRoom.id, currentFolder?.id || null);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete folder');
        }
      },
    });
  };

  const handleDownloadFolder = async (folder) => {
    toast.info(`Preparing "${folder.name}.zip"...`);
    try {
      const res = await foldersApi.download(folder.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folder.name}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${folder.name}.zip`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download folder');
    }
  };

  const openTemplateDialog = async () => {
    try {
      const res = await folderTemplatesApi.getAll();
      setTemplates(res.data);
      setIsTemplateDialogOpen(true);
    } catch { toast.error('Failed to load templates'); }
  };

  const handleApplyTemplate = async (templateId) => {
    if (!selectedRoom) return;
    setTemplateBusy(true);
    try {
      const res = await foldersApi.createFromTemplate({
        template_id: templateId,
        data_room_id: selectedRoom.id,
        parent_id: currentFolder?.id || null,
      });
      toast.success(`Created ${res.data.created_count} folders from "${res.data.template}"`);
      setIsTemplateDialogOpen(false);
      fetchFolders(selectedRoom.id, currentFolder?.id || null);
      fetchDataRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply template');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return toast.error('Please select a file');
    setUploading(true); setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      await documentsApi.upload(formData, {
        name: uploadFormData.name || selectedFile.name,
        description: uploadFormData.description,
        data_room_id: selectedRoom?.id || '',
        folder_id: currentFolder?.id || '',
        access_level: uploadFormData.access_level,
      });
      setUploadProgress(100);
      toast.success('File uploaded');
      setIsUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadFormData({ name: '', description: '', access_level: 'private' });
      fetchDocuments(selectedRoom?.id, currentFolder?.id || null);
      if (selectedRoom) fetchDataRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false); setUploadProgress(0);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsApi.download(doc.id);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || doc.name;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  const handlePreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl(null);
    setPreviewText(null);
    setIsPreviewOpen(true);
    // For non-previewable types, skip fetch and show download prompt in modal
    if (!isPreviewable(doc.content_type)) {
      return;
    }
    try {
      const res = await documentsApi.preview(doc.id);
      const ct = doc.content_type || '';
      if (ct.startsWith('text/')) {
        const text = await res.data.text();
        setPreviewText(text);
      } else {
        const url = URL.createObjectURL(res.data);
        setPreviewUrl(url);
      }
    } catch {
      toast.error('Failed to load preview');
      setIsPreviewOpen(false);
    }
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); }
    setPreviewUrl(null);
    setPreviewText(null);
    setPreviewDoc(null);
  };

  const openRename = (type, id, name) => {
    setRenameTarget({ type, id, name });
    setRenameValue(name || '');
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameTarget) return;
    const newName = renameValue.trim();
    if (!newName) return toast.error('Name cannot be empty');
    if (newName === renameTarget.name) { setRenameTarget(null); return; }
    setRenameBusy(true);
    try {
      if (renameTarget.type === 'document') {
        await documentsApi.rename(renameTarget.id, newName);
        toast.success('Document renamed');
        fetchDocuments(selectedRoom?.id, currentFolder?.id || null);
      } else {
        await foldersApi.rename(renameTarget.id, newName);
        toast.success('Folder renamed');
        fetchFolders(selectedRoom.id, currentFolder?.id || null);
        // If the renamed folder is also the current location, update breadcrumb
        if (currentFolder?.id === renameTarget.id) {
          setCurrentFolder({ ...currentFolder, name: newName });
          setBreadcrumb(breadcrumb.map((b) => b.id === renameTarget.id ? { ...b, name: newName } : b));
        }
      }
      setRenameTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    } finally {
      setRenameBusy(false);
    }
  };

  const handleDeleteDocument = async (id, name) => {
    setConfirm({
      title: `Delete "${name || 'this document'}"?`,
      description: 'This document will be removed. You can restore it later from the audit trail.',
      confirmLabel: 'Delete Document',
      onConfirm: async () => {
        try {
          await documentsApi.delete(id);
          toast.success('Document deleted');
          fetchDocuments(selectedRoom?.id, currentFolder?.id || null);
          if (selectedRoom) fetchDataRooms();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete document');
        }
      },
    });
  };

  const openShare = (type, id, name) => {
    setCreatedShare(null);
    setShareForm({ resource_type: type, resource_id: id, resource_name: name, allow_download: true, expires_in_days: 14 });
    setIsShareOpen(true);
  };

  const handleCreateShare = async () => {
    try {
      const res = await shareLinksApi.create({
        resource_type: shareForm.resource_type,
        resource_id: shareForm.resource_id,
        allow_download: shareForm.allow_download,
        expires_in_days: parseInt(shareForm.expires_in_days, 10) || 0,
      });
      setCreatedShare(res.data);
      toast.success('Share link created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
    }
  };

  const copyShareLink = (token) => {
    const url = `${window.location.origin}/share/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const filteredDocuments = documents.filter(d => !search || d.name.toLowerCase().includes(search.toLowerCase()));
  const filteredFolders = folders.filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()));

  // Drag & drop state
  const [dragItem, setDragItem] = useState(null); // { type: 'folder'|'document', id, name }
  const [dragOverId, setDragOverId] = useState(null); // folder id or 'root' or breadcrumb id

  // Desktop-drag file upload state
  const [externalDragging, setExternalDragging] = useState(false);
  const [bulkUploading, setBulkUploading] = useState({ active: false, done: 0, total: 0 });
  const dropzoneDragCounter = React.useRef(0);

  const onDragStart = (type, id, name) => (e) => {
    setDragItem({ type, id, name });
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', `${type}:${id}`); } catch { /* noop */ }
  };

  const onDragOver = (targetId) => (e) => {
    if (!dragItem) return;
    // Don't allow dropping onto self
    if (dragItem.type === 'folder' && dragItem.id === targetId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== targetId) setDragOverId(targetId);
  };

  const onDragLeave = () => setDragOverId(null);

  const performMove = async (targetFolderId) => {
    if (!dragItem) return;
    try {
      if (dragItem.type === 'folder') {
        await foldersApi.move(dragItem.id, targetFolderId);
        toast.success(`Moved folder "${dragItem.name}"`);
      } else {
        await documentsApi.move(dragItem.id, targetFolderId);
        toast.success(`Moved "${dragItem.name}"`);
      }
      fetchFolders(selectedRoom.id, currentFolder?.id || null);
      fetchDocuments(selectedRoom.id, currentFolder?.id || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Move failed');
    } finally {
      setDragItem(null);
      setDragOverId(null);
    }
  };

  const onDropOnFolder = (folder) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragItem) return;
    if (dragItem.type === 'folder' && dragItem.id === folder.id) return;
    performMove(folder.id);
  };

  const onDropOnBreadcrumb = (folderId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragItem) return;
    performMove(folderId);
  };

  const onDragEnd = () => { setDragItem(null); setDragOverId(null); };

  // ====== Desktop-drag upload (files from OS) ======
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBulkUploading({ active: true, done: 0, total: files.length });
    let success = 0;
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fd = new FormData();
      fd.append('file', file);
      try {
        await documentsApi.upload(fd, {
          name: file.name,
          data_room_id: selectedRoom?.id || '',
          folder_id: currentFolder?.id || '',
          access_level: 'private',
        });
        success++;
      } catch (err) {
        failed++;
        logger.error('Upload failed:', file.name, err);
      }
      setBulkUploading((prev) => ({ ...prev, done: i + 1 }));
    }
    setBulkUploading({ active: false, done: 0, total: 0 });
    if (success > 0) {
      toast.success(`Uploaded ${success} file${success > 1 ? 's' : ''}${failed ? ` (${failed} failed)` : ''}`);
      fetchDocuments(selectedRoom?.id, currentFolder?.id || null);
      if (selectedRoom) fetchDataRooms();
    } else if (failed > 0) {
      toast.error(`Failed to upload ${failed} file${failed > 1 ? 's' : ''}`);
    }
  };

  const isExternalFileDrag = (e) =>
    e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');

  const onDropzoneDragEnter = (e) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    dropzoneDragCounter.current += 1;
    setExternalDragging(true);
  };

  const onDropzoneDragLeave = (e) => {
    if (!isExternalFileDrag(e)) return;
    dropzoneDragCounter.current -= 1;
    if (dropzoneDragCounter.current <= 0) {
      dropzoneDragCounter.current = 0;
      setExternalDragging(false);
    }
  };

  const onDropzoneDragOver = (e) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDropzoneDrop = (e) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    dropzoneDragCounter.current = 0;
    setExternalDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) uploadFiles(files);
  };

  return (
    <div
      className="space-y-6 relative"
      data-testid="documents-page"
      onDragEnter={onDropzoneDragEnter}
      onDragLeave={onDropzoneDragLeave}
      onDragOver={onDropzoneDragOver}
      onDrop={onDropzoneDrop}
    >
      {/* Desktop drag overlay */}
      {externalDragging && (
        <div className="fixed inset-0 z-[60] bg-primary/10 backdrop-blur-sm flex items-center justify-center pointer-events-none" data-testid="dropzone-overlay">
          <div className="bg-card border-4 border-dashed border-primary rounded-2xl p-12 text-center max-w-md mx-4">
            <Upload size={48} className="mx-auto text-primary mb-3" />
            <p className="font-heading text-xl font-bold">Drop files to upload</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedRoom
                ? `Files will be saved to ${selectedRoom.name}${currentFolder ? ` › ${currentFolder.name}` : ''}`
                : 'Files will be saved to "All Documents"'}
            </p>
          </div>
        </div>
      )}

      {/* Bulk upload progress */}
      {bulkUploading.active && (
        <div className="fixed bottom-6 right-6 z-50 bg-card border border-border rounded-xl shadow-lg p-4 min-w-64" data-testid="bulk-upload-progress">
          <div className="flex items-center gap-3 mb-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <div>
              <p className="font-medium text-sm">Uploading files</p>
              <p className="text-xs text-muted-foreground">{bulkUploading.done} / {bulkUploading.total}</p>
            </div>
          </div>
          <Progress value={(bulkUploading.done / bulkUploading.total) * 100} className="h-2" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Documents</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {selectedRoom ? `${selectedRoom.name} • ${documents.length} docs, ${folders.length} folders` : 'All documents'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setIsRoomDialogOpen(true)} variant="outline" className="rounded-lg" data-testid="create-room-btn">
            <Folder size={18} className="mr-2" /> New Data Room
          </Button>
          {selectedRoom && (
            <>
              <Button onClick={() => setIsFolderDialogOpen(true)} variant="outline" className="rounded-lg" data-testid="create-folder-btn">
                <FolderPlus size={18} className="mr-2" /> {currentFolder ? 'New Subfolder' : 'New Folder'}
              </Button>
              <Button onClick={openTemplateDialog} variant="outline" className="rounded-lg" data-testid="from-template-btn">
                <MagicWand size={18} className="mr-2" /> From Template
              </Button>
            </>
          )}
          <Button onClick={() => setIsUploadDialogOpen(true)} className="rounded-lg" data-testid="upload-doc-btn">
            <Upload size={18} className="mr-2" /> Upload
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Data Rooms Sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0 space-y-2">
          <div
            className={`p-3 rounded-lg cursor-pointer transition-colors ${!selectedRoom ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:bg-muted'}`}
            onClick={() => { setSelectedRoom(null); setCurrentFolder(null); setBreadcrumb([]); }}
            data-testid="all-documents"
          >
            <div className="flex items-center gap-2"><File size={18} /><span className="font-medium">All Documents</span></div>
          </div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground px-3 pt-4">Data Rooms</p>
          {dataRooms.map((room) => (
            <div
              key={room.id}
              className={`p-3 rounded-lg cursor-pointer transition-colors group ${selectedRoom?.id === room.id ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:bg-muted'}`}
              onClick={() => openRoom(room)}
              data-testid={`room-${room.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Folder size={18} />
                  <span className="font-medium truncate">{room.name}</span>
                </div>
                <Button
                  variant="ghost" size="sm"
                  className={`h-6 w-6 p-0 opacity-0 group-hover:opacity-100 ${selectedRoom?.id === room.id ? 'text-primary-foreground hover:text-primary-foreground' : ''}`}
                  onClick={(e) => { e.stopPropagation(); handleDeleteRoom(room.id); }}
                >
                  <Trash size={14} />
                </Button>
              </div>
              <p className="text-xs mt-1 opacity-70">{room.document_count} documents</p>
              {room.sector && (
                <Badge variant="outline" className="mt-2 text-xs">
                  {PROCUREMENT_SECTORS.find(s => s.id === room.sector)?.label || room.sector}
                </Badge>
              )}
            </div>
          ))}
        </div>

        {/* Main Pane */}
        <div className="flex-1 space-y-4 min-w-0">
          {/* Breadcrumb */}
          {selectedRoom && (
            <div className="flex items-center gap-1 text-sm flex-wrap" data-testid="breadcrumb">
              <button
                onClick={() => goToBreadcrumb(-1)}
                onDragOver={onDragOver('root')}
                onDragLeave={onDragLeave}
                onDrop={onDropOnBreadcrumb(null)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors ${dragOverId === 'root' ? 'ring-2 ring-primary bg-primary/10 text-foreground' : ''}`}
                data-testid="breadcrumb-root"
              >
                <House size={14} /> {selectedRoom.name}
              </button>
              {breadcrumb.map((b, i) => (
                <React.Fragment key={b.id}>
                  <CaretRight size={12} className="text-muted-foreground" />
                  <button
                    onClick={() => goToBreadcrumb(i)}
                    onDragOver={onDragOver(b.id)}
                    onDragLeave={onDragLeave}
                    onDrop={onDropOnBreadcrumb(b.id)}
                    className={`px-2 py-1 rounded-md hover:bg-muted transition-colors ${i === breadcrumb.length - 1 ? 'font-semibold' : 'text-muted-foreground'} ${dragOverId === b.id ? 'ring-2 ring-primary bg-primary/10' : ''}`}
                  >
                    {b.name}
                  </button>
                </React.Fragment>
              ))}
              {dragItem && (
                <Badge variant="outline" className="ml-2 text-xs">
                  Dragging {dragItem.type}: {dragItem.name}
                </Badge>
              )}
            </div>
          )}

          {/* Search */}
          <div className="relative max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-lg" data-testid="search-documents" />
          </div>

          {/* Folders — grid or list (only inside a room) */}
          {selectedRoom && filteredFolders.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Folders</p>
                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setViewMode('grid')}
                    data-testid="view-grid-btn"
                  >
                    <SquaresFour size={14} />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setViewMode('list')}
                    data-testid="view-list-btn"
                  >
                    <Rows size={14} />
                  </Button>
                </div>
              </div>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filteredFolders.map((folder) => (
                    <div
                      key={folder.id}
                      draggable
                      onDragStart={onDragStart('folder', folder.id, folder.name)}
                      onDragEnd={onDragEnd}
                      onDragOver={onDragOver(folder.id)}
                      onDragLeave={onDragLeave}
                      onDrop={onDropOnFolder(folder)}
                      className={`bg-card border rounded-xl p-4 transition-all group cursor-pointer ${
                        dragOverId === folder.id
                          ? 'border-primary ring-2 ring-primary/30 bg-primary/5'
                          : 'border-border hover:border-primary hover:shadow-md'
                      } ${dragItem?.id === folder.id && dragItem?.type === 'folder' ? 'opacity-50' : ''}`}
                      onClick={() => enterFolder(folder)}
                      data-testid={`folder-${folder.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Folder size={20} className="text-primary" weight="fill" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{folder.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {folder.document_count || 0} docs · {folder.subfolder_count || 0} subfolders
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); openRename('folder', folder.id, folder.name); }}
                            data-testid={`rename-folder-${folder.id}`}
                            title="Rename"
                          >
                            <PencilSimple size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); handleDownloadFolder(folder); }}
                            data-testid={`download-folder-${folder.id}`}
                            title="Download as ZIP"
                          >
                            <Download size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); openShare('folder', folder.id, folder.name); }}
                            data-testid={`share-folder-${folder.id}`}
                            title="Share link"
                          >
                            <ShareNetwork size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive"
                            onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                            data-testid={`delete-folder-${folder.id}`}
                            title="Delete"
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Folder</th>
                        <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Documents</th>
                        <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Subfolders</th>
                        <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFolders.map((folder) => (
                        <tr
                          key={folder.id}
                          draggable
                          onDragStart={onDragStart('folder', folder.id, folder.name)}
                          onDragEnd={onDragEnd}
                          onDragOver={onDragOver(folder.id)}
                          onDragLeave={onDragLeave}
                          onDrop={onDropOnFolder(folder)}
                          onClick={() => enterFolder(folder)}
                          className={`border-b border-border hover:bg-muted/30 cursor-pointer transition-colors ${
                            dragOverId === folder.id ? 'bg-primary/5 ring-2 ring-primary/30' : ''
                          } ${dragItem?.id === folder.id && dragItem?.type === 'folder' ? 'opacity-50' : ''}`}
                          data-testid={`folder-row-${folder.id}`}
                        >
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <Folder size={18} className="text-primary flex-shrink-0" weight="fill" />
                              <span className="font-medium">{folder.name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-center text-sm text-muted-foreground">{folder.document_count || 0}</td>
                          <td className="p-3 text-center text-sm text-muted-foreground">{folder.subfolder_count || 0}</td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" title="Rename"
                                onClick={(e) => { e.stopPropagation(); openRename('folder', folder.id, folder.name); }}
                                data-testid={`rename-folder-${folder.id}`}
                              >
                                <PencilSimple size={16} />
                              </Button>
                              <Button variant="ghost" size="sm"
                                onClick={(e) => { e.stopPropagation(); handleDownloadFolder(folder); }}
                                data-testid={`download-folder-${folder.id}`}
                              >
                                <Download size={16} />
                              </Button>
                              <Button variant="ghost" size="sm"
                                onClick={(e) => { e.stopPropagation(); openShare('folder', folder.id, folder.name); }}
                                data-testid={`share-folder-${folder.id}`}
                              >
                                <ShareNetwork size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-destructive"
                                onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                                data-testid={`delete-folder-${folder.id}`}
                              >
                                <Trash size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Documents Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {selectedRoom && <div className="p-3 border-b border-border"><p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Documents</p></div>}
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Document</th>
                  <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Size</th>
                  <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Access</th>
                  <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Uploaded</th>
                  <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Loading...</td></tr>
                ) : filteredDocuments.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No documents found</td></tr>
                ) : (
                  filteredDocuments.map((doc) => {
                    const Icon = getFileIcon(doc.content_type);
                    return (
                      <tr
                        key={doc.id}
                        draggable
                        onDragStart={onDragStart('document', doc.id, doc.name)}
                        onDragEnd={onDragEnd}
                        className={`border-b border-border hover:bg-muted/30 transition-colors ${
                          dragItem?.id === doc.id && dragItem?.type === 'document' ? 'opacity-50' : ''
                        }`}
                        data-testid={`doc-row-${doc.id}`}
                      >
                        <td className="p-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePreview(doc); }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="flex items-center gap-3 text-left hover:text-primary transition-colors cursor-pointer"
                            data-testid={`doc-open-${doc.id}`}
                          >
                            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon size={18} className="text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{doc.name}</p>
                              <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                            </div>
                          </button>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{formatFileSize(doc.size)}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            {doc.access_level === 'private' && <Lock size={14} className="text-muted-foreground" />}
                            <span className="text-sm capitalize">{doc.access_level}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{formatDate(doc.created_at)}</td>
                        <td className="p-4" onMouseDown={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" title="Open / Preview"
                              onClick={(e) => { e.stopPropagation(); handlePreview(doc); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              data-testid={`preview-doc-${doc.id}`}
                            >
                              <Eye size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Share"
                              onClick={(e) => { e.stopPropagation(); openShare('document', doc.id, doc.name); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              data-testid={`share-doc-${doc.id}`}
                            >
                              <ShareNetwork size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Download"
                              onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              data-testid={`download-doc-${doc.id}`}
                            >
                              <Download size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Rename"
                              onClick={(e) => { e.stopPropagation(); openRename('document', doc.id, doc.name); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              data-testid={`rename-doc-${doc.id}`}
                            >
                              <PencilSimple size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Delete"
                              className="text-destructive hover:text-destructive"
                              onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id, doc.name); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              data-testid={`delete-doc-${doc.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Data Room Dialog */}
      <Dialog open={isRoomDialogOpen} onOpenChange={setIsRoomDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create Data Room</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateRoom} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={roomFormData.name} onChange={(e) => setRoomFormData({ ...roomFormData, name: e.target.value })} required data-testid="room-name" />
            </div>
            <div className="space-y-2">
              <Label>Sector</Label>
              <Select value={roomFormData.sector || 'none'} onValueChange={(v) => setRoomFormData({ ...roomFormData, sector: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="room-sector"><SelectValue placeholder="Select sector" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {PROCUREMENT_SECTORS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={roomFormData.description} onChange={(e) => setRoomFormData({ ...roomFormData, description: e.target.value })} rows={3} data-testid="room-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRoomDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="room-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{currentFolder ? `New subfolder in "${currentFolder.name}"` : 'New Folder'}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateFolder} className="space-y-4">
            <div className="space-y-2">
              <Label>Folder Name *</Label>
              <Input value={folderForm.name} onChange={(e) => setFolderForm({ name: e.target.value })} required autoFocus data-testid="folder-name" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFolderDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="folder-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            {selectedRoom && (
              <p className="text-xs text-muted-foreground pt-1">
                Destination: {selectedRoom.name}{currentFolder ? ` › ${currentFolder.name}` : ''}
              </p>
            )}
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>File *</Label>
              <Input type="file" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} required data-testid="upload-file" />
            </div>
            {selectedFile && (
              <p className="text-sm text-muted-foreground">Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})</p>
            )}
            <div className="space-y-2">
              <Label>Name (optional)</Label>
              <Input value={uploadFormData.name} onChange={(e) => setUploadFormData({ ...uploadFormData, name: e.target.value })} placeholder="Leave blank to use filename" data-testid="upload-name" />
            </div>
            <div className="space-y-2">
              <Label>Access Level</Label>
              <Select value={uploadFormData.access_level} onValueChange={(v) => setUploadFormData({ ...uploadFormData, access_level: v })}>
                <SelectTrigger data-testid="upload-access"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={uploadFormData.description} onChange={(e) => setUploadFormData({ ...uploadFormData, description: e.target.value })} rows={2} data-testid="upload-description" />
            </div>
            {uploading && <Progress value={uploadProgress} className="h-2" />}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)} disabled={uploading}>Cancel</Button>
              <Button type="submit" disabled={uploading} data-testid="upload-submit">{uploading ? 'Uploading...' : 'Upload'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Folder from Template Dialog */}
      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MagicWand size={20} /> Create folders from template
            </DialogTitle>
            {selectedRoom && (
              <p className="text-xs text-muted-foreground pt-1">
                Destination: {selectedRoom.name}{currentFolder ? ` › ${currentFolder.name}` : ''}
              </p>
            )}
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => handleApplyTemplate(t.id)}
                disabled={templateBusy}
                className="text-left bg-card border border-border hover:border-primary hover:shadow-md rounded-xl p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`template-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ListChecks size={20} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                    <Badge variant="outline" className="mt-2 text-xs">{t.folder_count} folders</Badge>
                  </div>
                </div>
              </button>
            ))}
            {templates.length === 0 && (
              <p className="col-span-2 text-center text-muted-foreground text-sm py-8">No templates available</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTemplateDialogOpen(false)} disabled={templateBusy}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="sm:max-w-5xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-4">
              <span className="truncate">{previewDoc?.name}</span>
              <div className="flex items-center gap-1 flex-shrink-0">
                {previewDoc && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => openShare('document', previewDoc.id, previewDoc.name)}>
                      <ShareNetwork size={14} className="mr-1" /> Share
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownload(previewDoc)}>
                      <Download size={14} className="mr-1" /> Download
                    </Button>
                  </>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-muted/30 rounded-lg" data-testid="preview-content">
            {previewDoc && !isPreviewable(previewDoc.content_type) && (
              <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <File size={56} className="text-muted-foreground opacity-40 mb-4" />
                <p className="font-medium mb-1">Preview not available</p>
                <p className="text-sm text-muted-foreground mb-4">
                  This file type ({previewDoc.content_type || 'unknown'}) can't be previewed in the browser.
                </p>
                <Button onClick={() => handleDownload(previewDoc)}>
                  <Download size={16} className="mr-2" /> Download to open
                </Button>
              </div>
            )}
            {previewDoc && isPreviewable(previewDoc.content_type) && !previewUrl && !previewText && (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Loading preview...</div>
            )}
            {previewText && (
              <pre className="p-4 text-sm font-mono whitespace-pre-wrap break-words">{previewText}</pre>
            )}
            {previewUrl && previewDoc?.content_type?.startsWith('image/') && (
              <div className="h-full flex items-center justify-center p-4">
                <img src={previewUrl} alt={previewDoc.name} className="max-h-full max-w-full object-contain" />
              </div>
            )}
            {previewUrl && previewDoc?.content_type === 'application/pdf' && (
              <iframe src={previewUrl} title={previewDoc.name} className="w-full h-full border-0" />
            )}
            {previewUrl && previewDoc?.content_type?.startsWith('video/') && (
              <div className="h-full flex items-center justify-center p-4">
                <video src={previewUrl} controls className="max-h-full max-w-full" />
              </div>
            )}
            {previewUrl && previewDoc?.content_type?.startsWith('audio/') && (
              <div className="h-full flex items-center justify-center p-8">
                <audio src={previewUrl} controls className="w-full" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Link Dialog */}
      <Dialog open={isShareOpen} onOpenChange={(v) => { if (!v) { setCreatedShare(null); } setIsShareOpen(v); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShareNetwork size={20} /> Share {shareForm.resource_type}
            </DialogTitle>
          </DialogHeader>
          {!createdShare ? (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Sharing</p>
                <p className="text-sm font-medium truncate">{shareForm.resource_name}</p>
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div>
                  <p className="text-sm font-medium">Allow download</p>
                  <p className="text-xs text-muted-foreground">Viewers can download the file(s)</p>
                </div>
                <Switch checked={shareForm.allow_download} onCheckedChange={(v) => setShareForm({ ...shareForm, allow_download: v })} data-testid="share-allow-download" />
              </div>
              <div className="space-y-2">
                <Label>Expires in (days, 0 = never)</Label>
                <Input type="number" min="0" value={shareForm.expires_in_days} onChange={(e) => setShareForm({ ...shareForm, expires_in_days: e.target.value })} data-testid="share-expires" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsShareOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateShare} data-testid="share-create-btn"><LinkSimple size={16} className="mr-1" /> Generate Link</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 border border-emerald-500/30 bg-emerald-500/5 rounded-lg" data-testid="share-created">
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-2">Share link ready</p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/share/${createdShare.token}`}
                    className="font-mono text-xs"
                    data-testid="share-url-input"
                  />
                  <Button size="sm" onClick={() => copyShareLink(createdShare.token)} data-testid="share-copy-btn">
                    <Copy size={14} />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {createdShare.expires_at ? `Expires ${formatDate(createdShare.expires_at)}` : 'Never expires'} · {createdShare.allow_download ? 'Download enabled' : 'View only'}
                </p>
              </div>
              <DialogFooter>
                <Button onClick={() => setIsShareOpen(false)} data-testid="share-done-btn">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(v) => !v && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="rename-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={20} /> Rename {renameTarget?.type}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="space-y-4">
            <div className="space-y-2">
              <Label>New name</Label>
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                autoFocus
                required
                maxLength={255}
                data-testid="rename-input"
              />
              {renameTarget && (
                <p className="text-xs text-muted-foreground">Current: <span className="font-mono">{renameTarget.name}</span></p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameTarget(null)} disabled={renameBusy}>Cancel</Button>
              <Button type="submit" disabled={renameBusy || !renameValue.trim()} data-testid="rename-submit">
                {renameBusy ? 'Renaming...' : 'Rename'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash size={20} /> {confirm?.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{confirm?.description}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="confirm-cancel">Cancel</Button>
            <Button
              variant="destructive"
              data-testid="confirm-ok"
              onClick={async () => {
                const action = confirm?.onConfirm;
                setConfirm(null);
                if (action) await action();
              }}
            >
              {confirm?.confirmLabel || 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
